import axios, {
  AxiosError,
  AxiosHeaders,
  AxiosInstance,
  InternalAxiosRequestConfig,
} from 'axios';
import baseUrl from '../config/baseURL';
import { getData } from './storage';

type ApiError = Error & {
  status?: number;
  data?: unknown;
};

const REQUEST_TIMEOUT_MS = 15000;

const applyInterceptors = (client: AxiosInstance) => {
  client.interceptors.request.use(
    async (config: InternalAxiosRequestConfig) => {
      const headers = AxiosHeaders.from(config.headers);
      const hasAuthorization = headers.has('Authorization');

      if (!hasAuthorization) {
        const token = await getData('access_token');
        if (typeof token === 'string' && token.length > 0) {
          headers.set('Authorization', `Bearer ${token}`);
        }
      }

      const method = config.method?.toLowerCase();
      const isMultipart = config.data instanceof FormData;

      if (
        !headers.has('Content-Type') &&
        method &&
        ['post', 'put', 'patch'].includes(method) &&
        !isMultipart
      ) {
        headers.set('Content-Type', 'application/json');
      }

      config.headers = headers;
      return config;
    },
  );

  client.interceptors.response.use(
    (response) => response,
    (error: AxiosError) => {
      const apiError = new Error(
        error.response?.statusText ||
          (typeof error.response?.data === 'string'
            ? error.response.data
            : error.message) ||
          'Request failed',
      ) as ApiError;

      apiError.status = error.response?.status;
      apiError.data = error.response?.data;

      return Promise.reject(apiError);
    },
  );
};

axios.defaults.baseURL = baseUrl;
axios.defaults.timeout = REQUEST_TIMEOUT_MS;
applyInterceptors(axios);

export const http = axios;

export const createApiClient = () => {
  const client = axios.create({
    baseURL: baseUrl,
    timeout: REQUEST_TIMEOUT_MS,
  });
  applyInterceptors(client);
  return client;
};

export default http;
